import { effect, ErrorHandler, inject, provideEnvironmentInitializer } from '@angular/core';
import { environment } from '../../../../environments/environment';
import * as Sentry from '@sentry/angular';
import { Router } from '@angular/router';
import { initSentry } from '../helper/init-sentry';
import { UserInfoService } from '../../user/services/user-info.service';
import { sentryAppState } from '../helper/sentry-app-state';
import { TranslocoService } from '@jsverse/transloco';
import { GlobalErrorHandler } from '../error-handler/global.error-handler';

export const provideSentry = () => {
  if (environment.monitoring.sentryDsn === null) {
    return [];
  }
  return [
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    // add this to make sure the TraceService is initialized somewhere
    provideEnvironmentInitializer(() => inject(Sentry.TraceService)),
    provideEnvironmentInitializer(() => {
      const userInfoService = inject(UserInfoService);
      if (!environment.monitoring.sentryDsn) {
        return;
      }
      const translocoService = inject(TranslocoService);
      effect(async () => {
        const enabled = userInfoService.monitoringTechnical();
        const lang = userInfoService.language();
        if (enabled && !sentryAppState.initiated) {
          await userInfoService.applyLanguage(lang);
          const version = await fetch('/version')
            .then((res) => res.json())
            .then((data) => data.version as string)
            .catch(() => undefined);
          initSentry(
            environment.monitoring.sentryDsn!,
            (key: string) => translocoService.translate(key),
            version,
          );
          sentryAppState.initiated = true;
        } else if (!enabled && sentryAppState.initiated) {
          Sentry.close();
          sentryAppState.initiated = false;
        }
      });
    }),
    provideEnvironmentInitializer(() => {
      const userInfoService = inject(UserInfoService);
      if (!environment.monitoring.sentryDsn) {
        return;
      }
      effect(async () => {
        const enabled = userInfoService.monitoringTechnical();
        const userInfo = userInfoService.info();
        if (enabled && userInfo) {
          Sentry.setUser({
            email: userInfo.email,
            id: userInfo.id,
            username: `${userInfo.firstName} ${userInfo.lastName}`,
          });
        }
      });
    }),
  ];
};
